/**
 * Vajra — the measured swing rule.
 *
 * THIS FILE IS A BYTE-IDENTICAL TWIN of
 * backend/src/services/strategies/vajraEngine.ts, enforced by
 * `node scripts/check-engine-parity.mjs`. Change one, change both.
 *
 * Every series it needs — BBR, Wave, ATR, and the higher timeframe's run
 * length — is an ARGUMENT, so this file imports nothing and can be run from
 * either side. That is what guarantees a future scan fires on the same bar the
 * chart marks.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE RULE
 *
 *   ENTRY   BBR <= -100 AND Wave <= -60 AND BBR rising against the previous bar
 *           — the shipped BW long, unchanged —
 *           AND the mapped HIGHER TIMEFRAME has extended at least 2 bars in its
 *           current direction since its last colour change.
 *   EXIT    BBR back to 0, or a 2.5 x ATR stop, or 20 bars. First one wins.
 *   SIZE    risk a fixed % of the account down to the stop.
 *
 * At an oversold entry the higher timeframe's run is almost always a DOWN run —
 * 1,019 of 1,025 occurrences on 4H, 227 of 227 on daily. So in plain terms the
 * gate is "the higher timeframe is falling too, and has been for a while". It is
 * a deeper-oversold condition across two timeframes, not a trend filter. Naming
 * it after higher highs would describe the half that never happens.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT WAS MEASURED
 *
 * docs/SWING-PREREGISTRATION.md, 47 crypto symbols, 487,000 four-hour bars,
 * 2019-2026, 0.20% round-trip costs, behind a permutation control that had to
 * clear its own false-discovery rate before any result was read.
 *
 *   4H, no gate   2,780 trades   +0.10% / trade   53.7% win
 *   4H, gated     1,054 trades   +0.93% / trade   56.9% win   3.32 /symbol/year
 *
 * The gate beat all 4,000 random selections of the same size, won 4 of 4
 * walk-forward folds, improved 36 of 47 symbols and beat the ungated rule in
 * seven of eight years. On daily crypto the same gate on the mapped higher
 * timeframe gives +4.54% against +2.92%.
 *
 * THE RESPONSE IS MONOTONE IN THE RUN LENGTH, which is the strongest argument
 * that it is real rather than a lucky cut:
 *
 *   run >= 0   2,780 trades   +0.10%   53.7% win     (no gate)
 *   run >= 1   1,928 trades   +0.19%   54.9% win
 *   run >= 2   1,054 trades   +0.93%   56.9% win     <- the DEFAULT
 *   run >= 3     496 trades   +1.85%   60.7% win
 *   run >= 4     242 trades   +2.84%   66.5% win
 *
 * The default is 2 because 2 is the value that was pre-registered and cleared
 * multiple-testing control. 3 and 4 look better and were NOT in the tested
 * family; they are offered as a setting, not as a claim.
 *
 * A NOTE ON WHY THIS ENGINE'S COUNT (1,054) EXCEEDS THE STUDY'S (1,019). The
 * study filtered the base rule's entries. This engine skips a gated-out signal
 * and is then FLAT, so it can take a later signal the base rule was still in a
 * trade for. 29 extra entries. The engine's behaviour is the correct one to
 * trade and its own numbers are what is quoted above.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT DID **NOT** WORK — recorded so it is not re-attempted
 *
 *   - THE HIGHER-TIMEFRAME BAND BEING GREEN. This measured +1.48% against
 *     +0.10% with a 100th-percentile null and 4/4 folds, and it was LOOK-AHEAD.
 *     The CLN higher-timeframe band stamps each bar with the higher-timeframe
 *     body CONTAINING it, and that body closes in the bar's future. Correct as
 *     drawn context, invalid as a gate. Made causal — the last higher-timeframe
 *     bar to have actually CLOSED — it gives +0.12% against +0.10%, i.e.
 *     nothing, in all three datasets. Any gate built on that band must read a
 *     CLOSED higher-timeframe bar or it is measuring hindsight.
 *   - THE HIGHER TIMEFRAME'S OSCILLATOR being oversold too: +0.45% against
 *     +0.10%, negative in three of eight years. On daily bars it looked
 *     spectacular (+10.20%) and that was entirely 2025-26.
 *   - A plain "close above EMA50" trend filter: -0.20% against +0.10%.
 *
 * THE STOP stays at 2.5 x ATR. On DAILY bars, walking each entry independently,
 * a wider 4.0 stop beat 2.5 in 11 of 13 market-years — but that does not carry
 * over here, and the reason is this engine's own sequencing: a held trade blocks
 * the next signal, so a wider stop holds longer and costs entries. Swept inside
 * the engine the gated rule peaks at 2.5 (1.5 -> +0.54%, 2.0 -> +0.71%,
 * 2.5 -> +0.93%, 3.0 -> +0.81%, 4.0 -> +0.61%). The daily finding stands for the
 * daily rule; it is not imported here on the strength of a different test.
 */

/** Minimal bar shape. CandleRow on either side is a structural superset, so
 *  either side's array can be passed straight in. */
export interface VajraBar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

/**
 * Run length per bar: how many consecutive bars have EXTENDED the current
 * direction since the last colour change. Reset to 0 on the colour flip itself
 * and on any bar that fails to extend.
 *
 * Defined here, in the twinned file, so the chart and the study cannot drift
 * into two definitions of the same word. Feed it the HIGHER-TIMEFRAME bars,
 * CLH-transformed and CLOSED.
 */
export function computeRunLengths(bars: VajraBar[]): number[] {
  const n = bars.length;
  const run = new Array<number>(n).fill(0);
  if (n < 2) return run;
  let cur = 0;
  for (let i = 1; i < n; i++) {
    const up = bars[i].close >= bars[i].open;
    const prevUp = bars[i - 1].close >= bars[i - 1].open;
    if (up !== prevUp) cur = 0;
    else if (up && bars[i].high > bars[i - 1].high) cur += 1;
    else if (!up && bars[i].low < bars[i - 1].low) cur += 1;
    else cur = 0;
    run[i] = cur;
  }
  return run;
}

export interface VajraOptions {
  /** BBR level at or below which the entry arms. Default -100. */
  bbOs: number;
  /** Wave (wt1) level at or below which the entry arms. Default -60. */
  wvOs: number;
  /** Require BBR to be rising against the previous bar. Default true. */
  requireTurn: boolean;
  /** Minimum higher-timeframe run length. 0 disables the gate entirely, which
   *  leaves the plain BW rule — break-even after costs on 4H bars. Default 2. */
  minHtfRun: number;
  /** BBR level that closes the trade. Default 0 (the midline). */
  exitLevel: number;
  /** Stop distance in ATR. Default 2.5; 0 disables the stop entirely. */
  stopAtr: number;
  /** Hard bar limit on a held trade. Default 20. */
  maxHold: number;
  /** Percent of account risked between entry and stop. Sizing only — this file
   *  places nothing. Default 1. */
  riskPct: number;
}

export const VAJRA_DEFAULTS: VajraOptions = {
  bbOs: -100,
  wvOs: -60,
  requireTurn: true,
  minHtfRun: 2,
  exitLevel: 0,
  stopAtr: 2.5,
  maxHold: 20,
  riskPct: 1,
};

export type VajraExitReason = 'level' | 'stop' | 'time';

export interface VajraTrade {
  entryIdx: number;
  entryTime: number;
  entryPx: number;
  /** Where the stop sits, fixed at entry from that bar's ATR. null when the
   *  stop is disabled or ATR was unavailable — in which case the trade cannot
   *  be sized and `qtyPerUnitEquity` is null too. */
  stopPx: number | null;
  /** Distance from entry to stop as a percent of entry — the per-trade risk. */
  riskPct: number | null;
  /** Position size per 1 unit of account equity: risk fraction divided by the
   *  stop distance. Multiply by the account value to get a quantity. */
  qtyPerUnitEquity: number | null;
  /** The higher-timeframe run length that admitted this entry. */
  htfRun: number | null;
  exitIdx: number | null;
  exitTime: number | null;
  exitPx: number | null;
  why: VajraExitReason | null;
  retPct: number | null;
}

/**
 * @param bars     price bars, ascending, CLOSED only
 * @param bbr      BBR100 per bar (null during warm-up)
 * @param wt1      WaveTrend wt1 per bar
 * @param atr      ATR(14) per bar
 * @param htfRun   the higher timeframe's run length as-of each bar, carried
 *                 from the last higher-timeframe bar that had already CLOSED.
 *                 null = no closed higher-timeframe context yet, and a null
 *                 NEVER passes the gate. The CALLER owns that causality: hand
 *                 in the run of the bar CONTAINING this one and the rule is
 *                 measuring hindsight.
 * @param warmup   first bar index any indicator is valid on
 * @param lastIdx  last bar that may be ACTED on (callers pass the last CLOSED bar)
 */
export function computeVajraTrades(
  bars: VajraBar[],
  bbr: Array<number | null>,
  wt1: number[],
  atr: Array<number | null>,
  htfRun: Array<number | null>,
  warmup: number,
  lastIdx: number,
  opts: VajraOptions,
): VajraTrade[] {
  const out: VajraTrade[] = [];
  const o = opts;
  const end = Math.min(lastIdx, bars.length - 1, bbr.length - 1, wt1.length - 1);
  const start = Math.max(1, warmup);
  if (end <= start) return out;

  const stopAtr = Math.max(0, o.stopAtr);
  const maxHold = Math.max(1, Math.floor(o.maxHold) || 20);
  const minRun = Math.max(0, Math.floor(o.minHtfRun));
  const riskFrac = Math.max(0, o.riskPct) / 100;

  let open: VajraTrade | null = null;
  let openAtr = 0;
  // Edge-triggering is computed on the UNGATED condition and the gate applied
  // at that bar. That is deliberate and it is what was measured: the study took
  // the base rule's entry bars and filtered them. Folding the gate into the
  // condition instead would let an entry slide forward to a later bar where the
  // gate happened to open, which is a different rule with a different record.
  let inLong = false;

  for (let i = start; i <= end; i++) {
    const b = bbr[i];
    const bp = bbr[i - 1];
    const w = wt1[i];
    if (b == null || bp == null || !isFinite(w)) continue;
    const px = bars[i].close;

    // Close before opening, so a single bar never does both.
    if (open) {
      const held = i - open.entryIdx;
      let why: VajraExitReason | null = null;
      if (stopAtr > 0 && openAtr > 0 && bars[i].low <= open.entryPx - stopAtr * openAtr) why = 'stop';
      else if (b >= o.exitLevel) why = 'level';
      else if (held >= maxHold) why = 'time';
      if (why) {
        open.exitIdx = i;
        open.exitTime = bars[i].time;
        open.exitPx = px;
        open.why = why;
        open.retPct = (px / open.entryPx - 1) * 100;
        out.push(open);
        open = null;
      }
    }

    const rawLong = b <= o.bbOs && w <= o.wvOs && (!o.requireTurn || b > bp);
    const run = htfRun[i];
    const gateOk = minRun <= 0 || (run != null && run >= minRun);

    if (!open && rawLong && !inLong && gateOk) {
      const a = atr[i] == null ? 0 : (atr[i] as number);
      const stopPx = stopAtr > 0 && a > 0 ? px - stopAtr * a : null;
      const riskPct = stopPx != null && px > 0 ? ((px - stopPx) / px) * 100 : null;
      open = {
        entryIdx: i,
        entryTime: bars[i].time,
        entryPx: px,
        stopPx,
        riskPct,
        qtyPerUnitEquity: stopPx != null && px > stopPx ? riskFrac / (px - stopPx) : null,
        htfRun: run,
        exitIdx: null,
        exitTime: null,
        exitPx: null,
        why: null,
        retPct: null,
      };
      openAtr = a;
    }
    inLong = rawLong;
  }

  if (open) out.push(open);
  return out;
}
